import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import {
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampFiltre, ChampRecherche } from '../components/ui/Onglets';
import { useToast } from '../components/ui/useToast';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import { useValeurDifferee } from '../lib/useRecherche';
import { dateReunion, jourCourt } from '../lib/useFormateurs';
import {
  useApprenantsRisque,
  useReglerSeuilAbsenteisme,
  type Absenteisme,
  type AffichageRisque,
  type LigneApprenantRisque,
  type SeuilAbsenteisme,
} from '../lib/useApprenantsRisque';
import { CHEMIN_ABSENTEISME } from '../lib/useAbsenteisme';

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function taux(valeur: number | null): string {
  return valeur === null ? '—' : `${String(valeur).replace('.', ',')} %`;
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

/** « 5 absences sur 10 demi-journées », justifiées dites à part. */
function decompte(lecture: Absenteisme): string {
  const base = `${pluriel(lecture.absences, 'absence')} sur ${pluriel(lecture.demiJournees, 'demi-journée')}`;
  return lecture.justifiees > 0
    ? `${base} · dont ${lecture.justifiees} justifiée${lecture.justifiees > 1 ? 's' : ''}`
    : base;
}

interface Filtres {
  afficher: AffichageRisque;
  recherche: string;
  formation: string;
  promotion: string;
}

const SANS_FILTRE: Filtres = {
  afficher: 'RISQUE',
  recherche: '',
  formation: '',
  promotion: '',
};

/**
 * Apprenants à risque (phase 5, TF-427) : les apprenants en formation dont
 * l'absentéisme dépasse le seuil réglé par la direction, avec un accès direct
 * à leur dossier.
 *
 * L'écran ne compte rien : taux, tendance, seuil de la formation et courrier
 * viennent de `/api/apprenants-risque`, qui relit l'émargement — feuilles
 * clôturées seules, comme le tableau de bord de la direction.
 */
export function DirApprenantsRisquePage(): JSX.Element {
  const { site } = usePerimetre();
  const [filtres, setFiltres] = useState<Filtres>(SANS_FILTRE);
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const recherche = useValeurDifferee(filtres.recherche.trim(), 300);
  const { data, isLoading, error } = useApprenantsRisque({
    site,
    ...filtres,
    recherche,
  });

  useEffect(() => setPage(1), [site, taillePage, recherche, filtres.afficher, filtres.formation, filtres.promotion]);

  const changer = <C extends keyof Filtres>(cle: C, valeur: Filtres[C]): void =>
    setFiltres((avant) => ({ ...avant, [cle]: valeur }));
  const filtreActif = filtres.recherche !== '' || filtres.formation !== '' || filtres.promotion !== '';
  const lignes = data?.apprenants ?? [];
  const affichees = lignes.slice((page - 1) * taillePage, page * taillePage);
  const compteurs = data?.compteurs;
  const seuil = data?.seuil;
  const promotions = (data?.options.promotions ?? []).filter(
    (promotion) => !filtres.formation || promotion.formation === filtres.formation,
  );

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Apprenants à risque"
        sousTitre="Apprenants en formation dont l’absentéisme dépasse le seuil · accès direct au dossier"
        actions={
          <>
            <Link
              to={CHEMIN_ABSENTEISME}
              className="inline-flex min-h-[44px] items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              Taux par session, module, formation
            </Link>
            <BarreSites />
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="À risque"
          valeur={compteurs?.aRisque ?? '—'}
          ton={compteurs && compteurs.aRisque > 0 ? 'red' : 'navy'}
          detail={seuil ? `Absentéisme au-delà de ${seuil.valeur} %` : 'Absentéisme au-delà du seuil'}
        />
        <Compteur
          libelle="Apprenants suivis"
          valeur={compteurs?.suivis ?? '—'}
          ton="mint"
          detail={
            compteurs && compteurs.sansEmargement > 0
              ? `En formation aujourd’hui · ${compteurs.sansEmargement} sans feuille clôturée`
              : 'En formation aujourd’hui'
          }
        />
        <Compteur
          libelle="Absentéisme global"
          valeur={compteurs ? taux(compteurs.absenteisme.taux) : '—'}
          ton="blue"
          detail={compteurs ? decompte(compteurs.absenteisme) : 'Feuilles clôturées des apprenants suivis'}
        />
        <Compteur
          libelle="Seuil d’absences atteint"
          valeur={compteurs?.seuilFormationAtteint ?? '—'}
          ton={compteurs && compteurs.seuilFormationAtteint > 0 ? 'warm' : 'navy'}
          detail="Seuil de la formation : courrier d’avertissement"
        />
      </div>

      {seuil && <CarteSeuil seuil={seuil} fenetre={data?.fenetreTendanceJours ?? 30} />}

      <div className="flex flex-col gap-2 rounded-2xl bg-white p-3 shadow-card sm:flex-row sm:flex-wrap sm:items-center sm:p-4">
        <div role="group" aria-label="Apprenants affichés" className="flex rounded-xl bg-gray-100 p-1">
          {(
            [
              ['RISQUE', `À risque${compteurs ? ` (${compteurs.aRisque})` : ''}`],
              ['TOUS', `Tous les suivis${compteurs ? ` (${compteurs.suivis})` : ''}`],
            ] as const
          ).map(([valeur, libelle]) => (
            <button
              key={valeur}
              type="button"
              aria-pressed={filtres.afficher === valeur}
              onClick={() => changer('afficher', valeur)}
              className={`min-h-[40px] flex-1 whitespace-nowrap rounded-lg px-3 text-xs font-semibold transition sm:flex-none ${
                filtres.afficher === valeur
                  ? 'bg-white text-codialis-navy shadow-sm'
                  : 'text-codialis-gray hover:text-codialis-navy'
              }`}
            >
              {libelle}
            </button>
          ))}
        </div>
        <ChampRecherche
          valeur={filtres.recherche}
          onChange={(valeur) => changer('recherche', valeur)}
          etiquette="Rechercher un apprenant"
          placeholder="Nom ou prénom…"
        />
        <ChampFiltre
          valeur={filtres.formation}
          onChange={(valeur) =>
            setFiltres((avant) => ({
              ...avant,
              formation: valeur,
              promotion: '',
            }))
          }
          etiquette="Formation"
        >
          <option value="">Toutes les formations</option>
          {data?.options.formations.map((formation) => (
            <option key={formation.code} value={formation.code}>
              {formation.titre}
            </option>
          ))}
        </ChampFiltre>
        <ChampFiltre
          valeur={filtres.promotion}
          onChange={(valeur) => changer('promotion', valeur)}
          etiquette="Promotion"
        >
          <option value="">Toutes les promotions</option>
          {promotions.map((promotion) => (
            <option key={promotion.cle} value={promotion.cle}>
              {promotion.libelle} · {promotion.siteCode}
            </option>
          ))}
        </ChampFiltre>
        {filtreActif && (
          <button
            type="button"
            onClick={() =>
              setFiltres((avant) => ({
                ...SANS_FILTRE,
                afficher: avant.afficher,
              }))
            }
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Réinitialiser
          </button>
        )}
      </div>

      {isLoading && <Chargement libelle="Chargement des apprenants…" />}
      {error && <MessageErreur message={messageErreur(error, 'La liste n’a pas pu être chargée.')} />}
      {!isLoading && !error && lignes.length === 0 && (
        <p className="rounded-2xl bg-white px-4 py-12 text-center text-sm text-codialis-gray shadow-card">
          {messageVide(filtres, compteurs?.suivis ?? 0, seuil?.valeur ?? null)}
        </p>
      )}

      {!isLoading && !error && lignes.length > 0 && (
        <div className="overflow-hidden rounded-2xl bg-white shadow-card">
          {/* Sous l'ordinateur, une carte par apprenant : le tableau y défilerait de côté. */}
          <ul className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0 xl:hidden">
            {affichees.map((ligne) => (
              <li key={ligne.id} className="space-y-3 border-gray-100 p-4 md:border-b md:odd:border-r">
                <Identite ligne={ligne} />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Taux ligne={ligne} />
                  <Tendance ligne={ligne} fenetre={data?.fenetreTendanceJours ?? 30} />
                  <SeuilFormation ligne={ligne} />
                  <Parcours ligne={ligne} />
                </div>
                <Link to={`/adm-dossier/${ligne.id}`} className={CLASSE_LIEN_FICHE}>
                  Ouvrir le dossier
                </Link>
              </li>
            ))}
          </ul>
          <div className="relative hidden overflow-x-auto xl:block">
            <table className="w-full min-w-[960px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {[
                    'Apprenant',
                    'Absentéisme',
                    `${data?.fenetreTendanceJours ?? 30} derniers jours`,
                    'Seuil de la formation',
                    'Formation · entreprise',
                    '',
                  ].map((colonne, index) => (
                    <th key={colonne || index} className={CLASSE_TH}>
                      {colonne || <span className="sr-only">Actions</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {affichees.map((ligne) => (
                  <tr key={ligne.id} className="transition hover:bg-gray-50">
                    <td className={`${CLASSE_TD} max-w-[240px]`}>
                      <Identite ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <Taux ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <Tendance ligne={ligne} fenetre={data?.fenetreTendanceJours ?? 30} />
                    </td>
                    <td className={`${CLASSE_TD} max-w-[220px]`}>
                      <SeuilFormation ligne={ligne} />
                    </td>
                    <td className={`${CLASSE_TD} max-w-[240px]`}>
                      <Parcours ligne={ligne} />
                    </td>
                    <td className={`${CLASSE_TD} text-right`}>
                      <Link to={`/adm-dossier/${ligne.id}`} className={CLASSE_LIEN_FICHE}>
                        Ouvrir le dossier
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pages={Math.max(1, Math.ceil(lignes.length / taillePage))}
            total={lignes.length}
            affiches={affichees.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="apprenant"
          />
        </div>
      )}

      {data && (
        <p className="text-[11px] text-codialis-gray">
          Feuilles d’émargement clôturées seules · un retard compte comme une présence · calculé le{' '}
          {new Date(data.calculeLe).toLocaleString('fr-FR', {
            timeZone: 'Indian/Reunion',
            dateStyle: 'short',
            timeStyle: 'short',
          })}
        </p>
      )}
    </div>
  );
}

function messageVide(filtres: Filtres, suivis: number, seuil: number | null): string {
  if (suivis === 0) return 'Aucun apprenant en formation dans ce périmètre.';
  if (filtres.recherche || filtres.formation || filtres.promotion) return 'Aucun apprenant ne correspond aux filtres.';
  return seuil === null
    ? 'Aucun apprenant au-delà du seuil d’absentéisme.'
    : `Aucun apprenant en formation au-delà de ${seuil} % d’absentéisme.`;
}

// ---------------------------------------------------------------------------
// Seuil d'absentéisme
// ---------------------------------------------------------------------------

/**
 * Le seuil de l'organisme, réglé ici par la direction — comme dans
 * « Paramétrage » (TF-435), qui tient le même réglage. Le seuil d'absences en
 * demi-journées de chaque formation, qui déclenche le courrier
 * d'avertissement, est une autre règle : il se règle dans « Paramétrage ».
 */
function CarteSeuil({ seuil, fenetre }: { seuil: SeuilAbsenteisme; fenetre: number }): JSX.Element {
  const toast = useToast();
  const regler = useReglerSeuilAbsenteisme();
  const [saisie, setSaisie] = useState(String(seuil.valeur));
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => setSaisie(String(seuil.valeur)), [seuil.valeur]);

  const valeur = Number(saisie);
  const inchange = saisie.trim() === '' || valeur === seuil.valeur;

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    regler.mutate(valeur, {
      onSuccess: (nouveau) => toast(`Seuil d’absentéisme réglé à ${nouveau.valeur} %`),
      onError: (refus) => setErreur(messageErreur(refus, 'Le seuil n’a pas pu être enregistré.')),
    });
  };

  return (
    <section className="flex flex-col gap-4 rounded-2xl bg-white p-4 shadow-card sm:p-5 lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0 space-y-1 lg:max-w-3xl">
        <h2 className="font-display text-base font-bold text-codialis-navy">Seuil d’absentéisme</h2>
        <p className="text-xs text-codialis-gray">
          Un apprenant en formation est à risque quand ses absences — justifiées comprises — dépassent ce pourcentage
          des demi-journées pointées, depuis le début de son parcours. Le taux des {fenetre} derniers jours est montré à
          côté. Le seuil d’absences de chaque formation, en demi-journées non justifiées, déclenche le courrier
          d’avertissement : il se règle dans « Paramétrage ».
        </p>
        <p className="text-[11px] text-codialis-gray">
          {seuil.parDefaut || !seuil.regleLe
            ? 'Valeur par défaut, jamais réglée.'
            : `Réglé${seuil.reglePar ? ` par ${seuil.reglePar}` : ''} le ${dateReunion(seuil.regleLe)}.`}
        </p>
      </div>
      <form onSubmit={enregistrer} className="flex shrink-0 flex-col gap-2 sm:items-end">
        <div className="flex items-center gap-2">
          <label htmlFor="seuil-absenteisme" className="text-sm font-medium text-codialis-navy">
            Au-delà de
          </label>
          <div className="relative">
            <input
              id="seuil-absenteisme"
              type="number"
              inputMode="numeric"
              min={seuil.min}
              max={seuil.max}
              step={1}
              required
              value={saisie}
              onChange={(evenement) => setSaisie(evenement.target.value)}
              aria-describedby={erreur ? 'seuil-absenteisme-erreur' : undefined}
              className="min-h-[44px] w-24 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-3 pr-8 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-codialis-gray">
              %
            </span>
          </div>
          <button
            type="submit"
            disabled={inchange || regler.isPending}
            className="min-h-[44px] rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-50"
          >
            {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
        {erreur && (
          <p id="seuil-absenteisme-erreur" role="alert" className="text-xs font-medium text-red-600">
            {erreur}
          </p>
        )}
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Ligne d'un apprenant
// ---------------------------------------------------------------------------

const CLASSE_TH = 'px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray xl:px-4';
const CLASSE_TD = 'px-3 py-3 align-top xl:px-4';
const CLASSE_LIEN_FICHE =
  'inline-flex min-h-[36px] items-center whitespace-nowrap text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]';

function Detail({ children }: { children: ReactNode }): JSX.Element {
  return <div className="text-[11px] text-codialis-gray">{children}</div>;
}

function Identite({ ligne }: { ligne: LigneApprenantRisque }): JSX.Element {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-xs font-bold text-white">
        {ligne.initiales}
      </span>
      <div className="min-w-0">
        <Link
          to={`/adm-dossier/${ligne.id}`}
          className="block truncate font-semibold text-codialis-navy hover:text-codialis-sky hover:underline [@media(pointer:coarse)]:leading-10"
        >
          {ligne.prenom} {ligne.nom}
        </Link>
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          <PastilleSite site={ligne.siteCode} />
          {ligne.aRisque && <Pastille texte="À risque" ton="danger" />}
        </div>
        <Detail>Référent : {ligne.referent?.nom ?? '—'}</Detail>
      </div>
    </div>
  );
}

function Taux({ ligne }: { ligne: LigneApprenantRisque }): JSX.Element {
  const { absenteisme } = ligne;
  // Un taux de 0 % se lirait comme une présence parfaite : sans feuille clôturée, on le dit.
  if (absenteisme.demiJournees === 0) return <Detail>Aucune demi-journée sur une feuille clôturée</Detail>;
  return (
    <div className="min-w-0 text-xs">
      <div className={`font-display text-lg font-bold ${ligne.aRisque ? 'text-red-600' : 'text-codialis-navy'}`}>
        {taux(absenteisme.taux)}
      </div>
      <Detail>{decompte(absenteisme)}</Detail>
      {ligne.derniereAbsence && <Detail>Dernière absence le {jourCourt(ligne.derniereAbsence)}</Detail>}
    </div>
  );
}

function Tendance({ ligne, fenetre }: { ligne: LigneApprenantRisque; fenetre: number }): JSX.Element {
  const { tendance } = ligne;
  return (
    <div className="min-w-0 text-xs">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-codialis-gray xl:hidden">
        {fenetre} derniers jours
      </div>
      {tendance.demiJournees === 0 ? (
        <Detail>Aucune demi-journée clôturée sur la période</Detail>
      ) : (
        <>
          <div className={`font-semibold ${tendance.auDelaDuSeuil ? 'text-red-600' : 'text-codialis-navy'}`}>
            {taux(tendance.taux)}
          </div>
          <Detail>{decompte(tendance)}</Detail>
        </>
      )}
    </div>
  );
}

function SeuilFormation({ ligne }: { ligne: LigneApprenantRisque }): JSX.Element {
  const { seuilFormation } = ligne;
  return (
    <div className="min-w-0 space-y-1 text-xs">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-codialis-gray xl:hidden">
        Seuil de la formation
      </div>
      <div className={seuilFormation.atteint ? 'font-semibold text-red-600' : 'text-codialis-navy'}>
        {seuilFormation.nonJustifiees}/{seuilFormation.seuil} demi-journée
        {seuilFormation.seuil > 1 ? 's' : ''} non justifiée
        {seuilFormation.nonJustifiees > 1 ? 's' : ''}
      </div>
      <div className="flex flex-wrap gap-1">
        {seuilFormation.courrierDu !== null && (
          <Pastille texte={`Courrier dû · palier ${seuilFormation.courrierDu}`} ton="warning" />
        )}
        {seuilFormation.dernierCourrier && (
          <Pastille
            texte={`Courrier du ${dateReunion(seuilFormation.dernierCourrier.le)} · palier ${seuilFormation.dernierCourrier.palier}`}
            ton="info"
          />
        )}
      </div>
    </div>
  );
}

function Parcours({ ligne }: { ligne: LigneApprenantRisque }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5 text-xs">
      <div className="font-medium text-codialis-navy">{ligne.formation?.titre ?? 'Sans formation rattachée'}</div>
      {ligne.promotion.libelle !== ligne.formation?.titre && <Detail>{ligne.promotion.libelle}</Detail>}
      <Detail>{ligne.entreprise ?? 'Sans entreprise'}</Detail>
    </div>
  );
}
